import {useState} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "../utils/toast";
import {
  addStepComment,
  assignUploadSide,
  completeStep,
  confirmDocumentPair,
  confirmExtraction,
  deleteStepDocumentUpload,
  downloadUpload,
  generateStepDocument,
  editExtractionField,
  ensureCustomerTelephone,
  invokeStepAction,
  saveDocumentRequest,
  saveStepSubmission,
  screenUploadAml,
  uploadStepDocument,
  uploadStepFile,
} from "../api";
import type { PaymentProofAnswers } from "../api";
import { wmErrorMessage } from "../api/wm-client";
import { CompleteStepBody } from "../dtos";

export interface SaveDraftInput {
  data: Record<string, unknown>;
  /** The client mobile carried by the form — its presence routes the save through crm-backend. */
  telephone?: string;
  /** §Autosave: a debounced background save — no success toast, and the nawy-now tree only
   * refetches when the engine says the save changed case facts (`caseChanged`), so typing never
   * triggers board-wide refetch churn. */
  quiet?: boolean;
}

/**
 * Write actions on one step. Every success invalidates BOTH the step detail and the
 * board query — completing a step flips downstream `blocked` flags, so the banner,
 * stepper and card statuses must refresh together.
 */
export const useStepMutations = ({
  stepUid,
  customerUid,
  paymentPlanUpdateVersion,
}: {
  stepUid: string;
  paymentPlanUpdateVersion?: number;
  customerUid?: string | null;
  caseUid?: string | null;
  participantUid?: string | null;
}) => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    // Refetch the WHOLE nawy-now tree (board, this step's detail, participants, case resolution)
    // so a submit reflects across the tab immediately — no manual page refresh. A single submit can
    // ripple beyond one query: completing Credit Review flips the case status badge + unblocks
    // downstream steps (board), a step can add a guarantor (participants), and a settled case
    // re-resolves (case-resolution). Matches useStartWorkflow / useParticipants, which already
    // invalidate the tree. `refetchType: "active"` (the v4 default) refetches every mounted query.
    queryClient.invalidateQueries({ queryKey: ["nawy-now"] });
  };

  const onError = (error: unknown) => toast.error(wmErrorMessage(error));

  const save = useMutation({
    // a form carrying the client mobile MUST first clear the crm-backend telephone check
    // (linked → pass, unknown → added, another customer's → 409 aborts); only then does
    // the submission go browser → workflow-manager directly, like every other save.
    // Never fall back to an unchecked save when the customer is unknown.
    mutationFn: async ({ data, telephone }: SaveDraftInput) => {
      if (telephone) {
        if (!customerUid) {
          throw new Error(
            "Couldn't verify the phone number against the CRM customer. Reload the page and try again."
          );
        }
        await ensureCustomerTelephone(customerUid, telephone);
      }
      return saveStepSubmission(stepUid, { data, ...(paymentPlanUpdateVersion === undefined ? {} : {paymentPlanUpdateVersion}) });
    },
    onSuccess: (result, variables) => {
      if (variables.quiet) {
        // §Autosave: silent unless the save moved case facts (preconditions / mirrors / gates) —
        // then the tab must refresh like an explicit save, or chips and hidden fields go stale.
        if ((result as { caseChanged?: boolean } | undefined)?.caseChanged) {
          invalidate();
        }
        return;
      }
      toast.success("Changes saved");
      invalidate();
    },
    onError,
  });

  const complete = useMutation({
    mutationFn: (body: CompleteStepBody) => completeStep(stepUid, {...body, ...(paymentPlanUpdateVersion === undefined ? {} : {paymentPlanUpdateVersion})}),
    onSuccess: () => {
      toast.success("Submitted");
      invalidate();
    },
    onError,
  });

  // §Comments: the WM per-step thread — same endpoint, same success copy as the WM UI.
  // §Escalate-overdue: `parentUid` makes it a reply on that comment's thread. Absent ⇒ a root note,
  // on the unchanged path — a plain comment gains no gate, no thread and no notification.
  const comment = useMutation({
    mutationFn: ({ content, parentUid, attachmentUids }: { content: string; parentUid?: string; attachmentUids?: string[] }) =>
      addStepComment(stepUid, content, parentUid, attachmentUids),
    onSuccess: (_result, variables) => {
      toast.success(variables.parentUid ? "Reply sent" : "Comment added");
      invalidate();
    },
    onError,
  });

  // §A3: non-terminal actions (Change Unit, send-feedback, …) fire effects without completing —
  // routing them through `complete` would APPROVE the step instead.
  const invokeAction = useMutation({
    mutationFn: ({ actionKey, comment, attachmentUids }: { actionKey: string; comment?: string; attachmentUids?: string[] }) =>
      invokeStepAction(stepUid, actionKey, comment, attachmentUids),
    onSuccess: () => {
      toast.success("Action applied");
      invalidate();
    },
    onError,
  });

  const upload = useMutation({
    mutationFn: ({
      fieldKey,
      file,
      isGlobalDocument,
      docType,
      replacesUploadUid,
      pairSlot,
      payment,
      comment,
      attachmentUids,
    }: {
      fieldKey: string;
      file: File;
      isGlobalDocument: boolean;
      /** §TypedDocs: the chosen type for a multi-type placement */
      docType?: string;
      /** §Replace: the live upload this file supersedes */
      replacesUploadUid?: string;
      /** §Two-sided: which face of whose card; omitted pairUid starts a new owner's pair */
      pairSlot?: { side: "front" | "back"; pairUid?: string };
      /** §paymentProof: the payment answers a paymentProof placement demands with the file */
      payment?: PaymentProofAnswers;
      /** §Late-docs-note: the optional note sales attached to a late addition (document route only) */
      comment?: string;
      attachmentUids?: string[];
    }): Promise<unknown> =>
      isGlobalDocument
        ? uploadStepDocument(stepUid, fieldKey, file, docType, replacesUploadUid, pairSlot, payment, comment, attachmentUids)
        : uploadStepFile(stepUid, file, fieldKey),
    onSuccess: () => {
      toast.success("File uploaded");
      invalidate();
    },
    onError,
  });

  // §OCR review: correct one extracted value — the engine re-opens the confirm state and
  // re-evaluates preconditions (extracted values are case facts).
  const editExtraction = useMutation({
    mutationFn: ({
      docKey,
      ocrFieldKey,
      value,
      uploadUid,
      pairUid,
    }: {
      docKey: string;
      ocrFieldKey: string;
      value: unknown;
      uploadUid?: string;
      /** §Two-sided: scope the correction to one OWNER's pair (required with several owners) */
      pairUid?: string;
    }) => editExtractionField(stepUid, docKey, ocrFieldKey, value, uploadUid, pairUid),
    onSuccess: invalidate,
    onError,
  });

  const confirmDocExtraction = useMutation({
    mutationFn: ({ docKey }: { docKey: string }) => confirmExtraction(stepUid, docKey),
    onSuccess: () => {
      toast.success("Extracted values confirmed");
      invalidate();
    },
    onError,
  });

  // §Doc-request: record (or clear) which extra documents this placement needs — FROM_CLIENT
  // notifies the customer, so say when that happened.
  const docRequest = useMutation({
    mutationFn: ({
      docKey,
      mode,
      items,
      attachmentUids,
    }: {
      docKey: string;
      mode: "ON_BEHALF" | "FROM_CLIENT";
      items: { category: string; type: string }[];
      attachmentUids?: string[];
    }) => saveDocumentRequest(stepUid, { fieldKey: docKey, mode, items, attachmentUids }),
    onSuccess: (result, variables) => {
      toast.success(
        variables.items.length === 0
          ? "Request cleared"
          : result.notified
            ? "Request saved — client notified"
            : "Request saved"
      );
      invalidate();
    },
    onError,
  });

  // §Two-sided: confirm ONE owner's front/back pair — per owner, never per document.
  const confirmPair = useMutation({
    mutationFn: ({ docKey, pairUid }: { docKey: string; pairUid: string }) =>
      confirmDocumentPair(stepUid, docKey, pairUid),
    onSuccess: () => {
      toast.success("Owner's values confirmed");
      invalidate();
    },
    onError,
  });

  // §Two-sided: file a legacy side-less upload as a front or back — it blocks completion until
  // assigned (or deleted), so the invalidation refreshes the blockers with the card.
  const assignSide = useMutation({
    mutationFn: ({
      docKey,
      uploadUid,
      side,
      pairUid,
    }: {
      docKey: string;
      uploadUid: string;
      side: "front" | "back";
      pairUid?: string;
    }) => assignUploadSide(stepUid, docKey, uploadUid, side, pairUid),
    onSuccess: (result) => {
      toast.success(`Filed as the ${result.side}`);
      invalidate();
    },
    onError,
  });

  const amlScreen = useMutation({
    mutationFn: ({ docKey, uploadUid }: { docKey: string; uploadUid: string }) =>
      screenUploadAml(stepUid, docKey, uploadUid),
    onSuccess: invalidate,
    onError,
  });

  const removeUpload = useMutation({
    mutationFn: ({
      docKey,
      uploadUid,
    }: {
      docKey: string;
      uploadUid: string;
    }) => deleteStepDocumentUpload(stepUid, docKey, uploadUid),
    onSuccess: invalidate,
    onError,
  });

  const [downloading, setDownloading] = useState<string[]>([]);
  const download = async (uploadUid: string, fileName: string) => {
    setDownloading(current => [...current, uploadUid]);
    try {
      await downloadUpload(uploadUid, fileName);
    } catch (error) {
      toast.error(wmErrorMessage(error));
    } finally {
      setDownloading(current => current.filter(uid => uid !== uploadUid));
    }
  };

  /**
   * §KYC-form: render the placement's document from the step's saved answers and download it.
   *
   * This WRITES — the server files the rendered docx on the card as a SYSTEM upload — so it
   * invalidates like the mutations do rather than sitting beside `download` as a pure read.
   * Resolves to the saved filename, or null when the request failed (the toast already fired).
   */
  const generate = (docKey: string, fallbackName: string) =>
    generateStepDocument(stepUid, docKey, fallbackName)
      .then((fileName) => {
        invalidate();
        return fileName;
      })
      .catch((error) => {
        toast.error(wmErrorMessage(error));
        return null;
      });

  // `refresh` is the same invalidation the mutations run, exposed for writes that happen OUTSIDE
  // them — e.g. the compoundCheck widget calling the engine's own endpoint: it changes the routing
  // fact, the submit gate and which documents are revealed, none of which this hook can see.
  return {
    save,
    complete,
    comment,
    invokeAction,
    upload,
    removeUpload,
    download,
    downloading,
    generate,
    editExtraction,
    confirmDocExtraction,
    confirmPair,
    assignSide,
    amlScreen,
    docRequest,
    refresh: invalidate,
    stepUid,
  };
};
